import { AdditionalMedia, Breakpoints, Noiser, SrcSettings, CustomParam, ObjectType, PoiParams } from './types'

export const getAdditionalMediaDefaults = (
  breakpoints: Breakpoints,
  src: string,
  maxWidth?: number
): AdditionalMedia[] => {
  const breakpointWidthMob = breakpoints['max-width'].xs
  const breakpointWidthMobNumber = parseFloat(breakpointWidthMob)
  const breakpointWidthTab = breakpoints['max-width'].md
  const breakpointWidthTabNumber = parseFloat(breakpointWidthTab)
  return [
    {
      src,
      query: `(max-width: ${breakpointWidthMob})`,
      maxWidth: maxWidth || breakpointWidthMobNumber
    },
    {
      src,
      query: `(max-width: ${breakpointWidthTab})`,
      maxWidth: maxWidth || breakpointWidthTabNumber
    }
  ]
}

export const getSrcSettings = (
  src: string,
  width: number | string,
  height: number | string,
  quality: number,
  templates: string[],
  fallbackFmt: string,
  cdn?: boolean,
  noiser?: Noiser,
  maxWidth?: number | string,
  maxHeight?: number | string,
  type?: string
): SrcSettings => {
  if (!cdn) {
    return {
      src,
      srcSet: `${src} 1x, ${src} 2x`
    }
  }

  const supportedResolutions = [1, 2]
  let srcSet = ''
  let srcSetWebp = ''
  let srcSetAvif = ''

  const smartImagesFormat = 'auto'
  const templatesParam = templates.length ? `${templates.join('&')}&` : ``
  const maxWidthExists = typeof maxWidth !== 'undefined'
  const maxHeightExists = typeof maxHeight !== 'undefined'
  const widthExists = typeof width !== 'undefined'
  const heightExists = typeof height !== 'undefined'

  const qParamPrefix = src?.includes('?') ? '&' : '?'

  const getUrlParams = (fmt: string) =>
    `${qParamPrefix}${
      !heightExists && width !== 'auto' ? 'w=auto' : ''
    }&${templatesParam}qlt=${quality}&fmt=${fmt}&noiser=${noiser}&`

  // default chrome/safari params
  const imageUrlWebpParams = getUrlParams('webp')
  const imageUrlAvifParams = getUrlParams('avif')

  // adding a default w=auto to the url when height is undefined for a fallback when poi exists.
  const qlt = type !== 'm0015' ? '.qlt=40' : ''
  const imageUrlParams = `${getUrlParams(smartImagesFormat)}${fallbackFmt}&fmt.jp2${qlt}&`

  // maxW/maxH parameter is also appended to the image url when present
  const maxWParamForSrc = maxWidthExists ? `maxW=${maxWidth}&` : ''
  const maxHParamForSrc = maxHeightExists ? `maxH=${maxHeight}&` : ''
  const widthParamForSrc = widthExists ? `w=${width}&` : ''
  const heightParamForSrc = heightExists ? `h=${height}&` : ''

  supportedResolutions.forEach((res) => {
    let maxWParam = maxWParamForSrc
    let maxHParam = maxHParamForSrc
    let widthParam = widthParamForSrc
    let heightParam = heightParamForSrc
    // when maxW/maxH is present, they will get appended to the amplience url
    // we should avoid receiving NaN when auto value is passed
    if (maxWidthExists && maxWidth !== 'auto') {
      maxWParam = `maxW=${parseFloat(maxWidth as string)}&`
    }
    if (maxHeightExists && maxHeight !== 'auto') {
      maxHParam = `maxH=${parseFloat(maxHeight as string)}&`
    }
    if (widthExists && width !== 'auto') {
      widthParam = `w=${parseFloat(width as string) * res}&`
    }
    if (heightExists && height !== 'auto') {
      heightParam = `h=${parseFloat(height as string) * res}&`
    }

    srcSetAvif += `${src}${imageUrlAvifParams}${widthParam}${heightParam}${maxWParam}${maxHParam} ${res}x, `
    // setting a webp srcSet to serve both chrome/safari as jp2 file sizes are high
    srcSetWebp += `${src}${imageUrlWebpParams}${widthParam}${heightParam}${maxWParam}${maxHParam} ${res}x, `

    // set auto for images (Amplience smart images), but the jp2 quality (40) has been configured as a default instead of jpeg as it gives better compression
    // progressive jpeg settings is also configured within the srcSet as we do not require a separate source
    srcSet += `${src}${imageUrlParams}${widthParam}${heightParam}${maxWParam}${maxHParam} ${res}x, `
  })

  return {
    src: `${src}${imageUrlParams}${widthParamForSrc}${heightParamForSrc}${maxWParamForSrc}${maxHParamForSrc}`,
    srcSet,
    srcSetWebp,
    srcSetAvif
  }
}

export const createPOIparam = (
  imageRatio: number | string | null,
  url?: string,
  breakpoints?: ObjectType,
  custom?: CustomParam
): PoiParams[] | string => {
  const getWidth = (breakpointWidth: string): number => {
    const breakpointNumberWidth = parseFloat(breakpointWidth)
    const shouldSplit = custom?.shouldSplit(breakpointNumberWidth)
    return shouldSplit ? breakpointNumberWidth / 2.5 : breakpointNumberWidth / 2
  }

  const poiQuery = (ratio: number | string): string =>
    `poi={$this.metadata.pointOfInterest.x},{$this.metadata.pointOfInterest.y},{$this.metadata.pointOfInterest.w},{$this.metadata.pointOfInterest.h}&scaleFit={($this.metadata.pointOfInterest.x>0)?$sfpoi:$sfcenter}&sm=aspect&aspect=${ratio}&sfcenter=center&sfpoi=poi`

  const templates =
    breakpoints &&
    Object.keys(breakpoints).map((breakpoint) => {
      return {
        src: url,
        query: `(max-width: ${breakpoints[breakpoint]})`,
        width: getWidth(breakpoints[breakpoint]),
        templates: [
          `maxW=${parseInt(breakpoints[breakpoint], 10)}`,
          poiQuery(custom?.ratioMap[breakpoint] || imageRatio)
        ]
      }
    })

  return templates || poiQuery(imageRatio)
}
